import { Fade, Tooltip as MuiTooltip } from '@mui/material'
import { PopperProps } from '@mui/material/Popper'
import cn from 'classnames'
import {
  styles,
  TooltipContainer,
  tooltipOffset,
} from 'ext-ui/components/Tooltip/Tooltip.style'
import React, { ReactElement, SyntheticEvent } from 'react'

export type TooltipPlacement = PopperProps['placement']

export type TooltipProps = {
  open?: boolean
  tooltipContent: React.ReactNode
  arrow?: boolean
  variant?: 'default' | 'spotlight' | 'light'
  placement?: TooltipPlacement
  className?: string
  onOpen?: (event: SyntheticEvent<Element, Event>) => void
  onClose?: (event: Event | SyntheticEvent<Element, Event>) => void
  enterDelay?: number
  exitDelay?: number
  disableInteractive?: boolean
}

type TooltipComponentProps = TooltipProps &
  React.HTMLAttributes<HTMLDivElement> & {
    children: React.ReactElement
  }

function Tooltip(props: TooltipComponentProps): ReactElement {
  const {
    open,
    tooltipContent,
    variant = 'default',
    placement = 'top',
    arrow = true,
    enterDelay = 100,
    exitDelay = 50,
    disableInteractive = true,
    className,
    children,
    ...divProps
  } = props

  // validate tooltipContent to ensure it's a valid ReactNode
  const isValidTooltipContent =
    tooltipContent !== undefined &&
    tooltipContent !== null &&
    (typeof tooltipContent === 'string' ||
      typeof tooltipContent === 'number' ||
      React.isValidElement(tooltipContent))

  if (!isValidTooltipContent) {
    return <>{children}</>
  }

  return (
    <MuiTooltip
      open={open}
      css={styles}
      className={className}
      title={tooltipContent}
      enterDelay={enterDelay}
      leaveDelay={exitDelay}
      slots={{
        transition: Fade,
        tooltip: React.forwardRef<HTMLDivElement>((props, ref) => (
          <TooltipContainer
            {...props}
            ref={ref}
            className={cn(className, variant, placement, { arrow })}
          >
            {tooltipContent}
          </TooltipContainer>
        )),
      }}
      slotProps={{
        transition: {
          timeout: exitDelay === 0 ? 0 : 200,
        },
        popper: {
          sx: {
            /** TODO: odd edge case where if something automatically opens while a user is hovering over a tooltip the tooltip could show over top of it
             *  Not sure what the alternative would be that wouldn't stop the tooltip form being usable in popups, since the popper is a universal fixed overlay
             */
            zIndex: 1001,
          },
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: ({
                  placement,
                }: {
                  placement: PopperProps['placement']
                }) => tooltipOffset(placement),
              },
            },
          ],
        },
      }}
      arrow={arrow}
      placement={placement}
      disableInteractive={disableInteractive}
      onOpen={props.onOpen}
      onClose={props.onClose}
      {...divProps}
    >
      {children}
    </MuiTooltip>
  )
}

export default Tooltip
